import { useLocation, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Check, MapPin, Calendar } from 'lucide-react';
import { SEO_PAGES, PROJECTS } from '../data/content';

export default function SeoPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, '');
  const data = SEO_PAGES[slug];

  if (!data) return <Navigate to="/" replace />;

  const related = (data.relatedProjects || [])
    .map((rs) => PROJECTS.find((p) => p.slug === rs))
    .filter(Boolean);

  return (
    <div className="bg-white">
      <section className="relative pt-44 pb-20 bg-[#0D1B2A] grain overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${data.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-[#0D1B2A]/82" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="overline text-white/80 flex items-center gap-3 mb-6">
            <span className="gold-rule" /> {data.overline}
          </div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            {data.headline}
          </h1>
          <p className="mt-8 text-white/75 text-lg max-w-3xl leading-relaxed">
            {data.description}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">
              Discuss a Project <ArrowRight size={16} />
            </Link>
            {data.externalUrl && (
              <a href={data.externalUrl} target="_blank" rel="noreferrer" className="btn-secondary">
                {data.externalLabel} <ExternalLink size={15} />
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="overline mb-4 flex items-center gap-3">
              <span className="gold-rule" /> Capabilities
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0D1B2A] tracking-tight leading-tight">
              Engineering, delivery and commissioning — under one roof.
            </h2>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {data.capabilities.map((cap) => (
                <motion.li
                  key={cap}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-2.5 text-[15px] text-[#0D1B2A] border-l-2 border-[#D4AF37] pl-4 py-1.5"
                >
                  <Check size={16} className="text-[#D4AF37] shrink-0" />
                  {cap}
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-[#F5F7FA] p-7 border border-[#0D1B2A]/8">
              <div className="overline mb-4 flex items-center gap-3">
                <span className="gold-rule" /> Why Clan Group
              </div>
              <ul className="space-y-4 text-[14px] text-[#0D1B2A]">
                <li className="flex gap-3">
                  <span className="font-stat font-extrabold text-[#D4AF37] text-2xl leading-none">
                    01
                  </span>
                  <span>Integrated EPC capability — engineering, procurement & construction.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-stat font-extrabold text-[#D4AF37] text-2xl leading-none">
                    02
                  </span>
                  <span>International delivery experience across India, Africa & Middle East.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-stat font-extrabold text-[#D4AF37] text-2xl leading-none">
                    03
                  </span>
                  <span>Specialist group companies for cross-discipline execution.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-stat font-extrabold text-[#D4AF37] text-2xl leading-none">
                    04
                  </span>
                  <span>Uncompromising HSE, QA/QC and project governance.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[#F5F7FA] py-20 md:py-24">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <div className="overline mb-4 flex items-center gap-3">
              <span className="gold-rule" /> Related Projects
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0D1B2A] tracking-tight">
              See it delivered.
            </h2>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <article
                  key={p.slug}
                  className="group bg-white border border-[#0D1B2A]/10 card-hover-lift overflow-hidden"
                >
                  <div className="h-52 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">
                      {p.industry}
                    </div>
                    <h3 className="mt-2 font-display font-bold text-lg text-[#0D1B2A]">{p.name}</h3>
                    <div className="mt-3 flex gap-4 text-xs text-[#4B5563]">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={12} /> {p.location}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={12} /> {p.year}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#0D1B2A] py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-3 flex items-center gap-3">
              <span className="gold-rule" /> Get a Proposal
            </div>
            <h3 className="font-display text-white text-3xl md:text-4xl font-bold tracking-tight">
              Ready to engineer your next project with Clan Group?
            </h3>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">
              Talk to our team <ArrowRight size={16} />
            </Link>
            <Link to="/projects" className="btn-secondary">
              See projects <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
